import { G } from '@mobily/ts-belt';
import { Box, Chip, Typography } from '@mui/material';
import { get } from 'lodash';
import { RaRecord, useRecordContext, useTranslate } from 'react-admin';

import { B2bSavingPlanCategory, B2bSavingPlanCategoryValues } from '@Common/types/b2b/plans';
import { B2bContractType } from '@Common/types/bo/b2b/contracts/contract_type';
import { getPlanCategoryShortName } from '@Common/types/bo/b2b/plans';

interface ProductsFieldProps {
  source?: string;
  record?: RaRecord;
  hasNoCommonCashSources?: boolean;
  hasDifferentAgreementDistributions?: boolean;
  plansHaveCampaignTypeAsCashSource?: Record<string, boolean>;
  'data-testid'?: string;
}

export const ProductsField = ({
  source,
  record: recordFromProps,
  hasNoCommonCashSources,
  hasDifferentAgreementDistributions,
  plansHaveCampaignTypeAsCashSource,
}: ProductsFieldProps) => {
  const recordFromContext = useRecordContext();
  const translate = useTranslate();
  const record = recordFromProps || recordFromContext;
  const products = source ? get(record, source) : [];
  const planLabels = get(record, 'plan_labels') as string[] | undefined;
  const contractTypes = get(record, 'contract_types') as B2bContractType[] | undefined;

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {products &&
        products.map((category: B2bSavingPlanCategory, idx: number) => {
          if (!B2bSavingPlanCategoryValues.includes(category)) {
            return null;
          }
          const planLabel =
            G.isNullable(planLabels) || G.isNullable(planLabels[idx])
              ? ''
              : ` (${planLabels[idx]})`;

          const keyToUse = category + planLabel;

          const labelToUse = getPlanCategoryShortName(category, contractTypes) + planLabel;

          return (
            <Chip
              key={idx}
              label={labelToUse}
              disabled={
                plansHaveCampaignTypeAsCashSource && !plansHaveCampaignTypeAsCashSource[keyToUse]
              }
            />
          );
        })}
      {hasNoCommonCashSources && (
        <Typography sx={{ color: 'error.main' }} variant="subtitle2">
          {translate(
            'resources.bo_common.b2b.campaigns.sections.general.errors.no_common_cash_sources',
          )}
        </Typography>
      )}
      {hasDifferentAgreementDistributions && (
        <Typography sx={{ color: 'error.main' }} variant="subtitle2">
          {translate(
            'resources.bo_common.b2b.campaigns.sections.general.errors.different_agreement_distributions',
          )}
        </Typography>
      )}
    </Box>
  );
};
